import { useState, useContext, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Button,
  Select,
  MenuItem,
  FormControl,
  Chip,
  CircularProgress,
  alpha,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InventoryIcon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme as customTheme } from '../../theme/colors';

// Types for stock check response
export interface StockCheckItemLocation {
  locationId: string;
  locationName: string;
  available: number;
}

export interface StockCheckItem {
  productId: string;
  productName: string;
  requiredQuantity: number;
  availableAtLocation: number;
  shortfall: number;
  otherLocationsWithStock: StockCheckItemLocation[];
}

export interface OrderStockCheck {
  orderId: string;
  locationId: string;
  locationName: string;
  items: StockCheckItem[];
  canConfirm: boolean;
  requiresTransfer: boolean;
}

export interface TransferRequest {
  productId: string;
  fromLocationId: string;
  quantity: number;
}

interface OrderStockValidationPanelProps {
  stockCheck: OrderStockCheck;
  onConfirm: () => void;
  onRequestTransfer: (transferRequests: TransferRequest[]) => void;
  isConfirming?: boolean;
}

export const OrderStockValidationPanel = ({
  stockCheck,
  onConfirm,
  onRequestTransfer,
  isConfirming = false,
}: OrderStockValidationPanelProps) => {
  const { mode } = useContext(ColorModeContext);

  // Track selected source locations for transfers
  const [transferSelections, setTransferSelections] = useState<Record<string, string>>({});

  // Initialize selections for items with shortfall
  useEffect(() => {
    const initialSelections: Record<string, string> = {};
    stockCheck.items.forEach(item => {
      if (item.shortfall > 0 && item.otherLocationsWithStock.length > 0) {
        // Auto-select the location with highest stock
        const bestLocation = item.otherLocationsWithStock.reduce((prev, current) =>
          current.available > prev.available ? current : prev
        );
        initialSelections[item.productId] = bestLocation.locationId;
      }
    });
    setTransferSelections(initialSelections);
  }, [stockCheck]);

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const handleSelectionChange = (productId: string, locationId: string) => {
    setTransferSelections(prev => ({
      ...prev,
      [productId]: locationId,
    }));
  };

  const handleConfirmWithTransfers = () => {
    const transferRequests: TransferRequest[] = stockCheck.items
      .filter(item => item.shortfall > 0 && transferSelections[item.productId])
      .map(item => ({
        productId: item.productId,
        fromLocationId: transferSelections[item.productId],
        quantity: item.shortfall,
      }));

    onRequestTransfer(transferRequests);
  };

  // Check if all shortfall items have valid transfer selections
  const allShortfallsResolved = stockCheck.items
    .filter(item => item.shortfall > 0)
    .every(item => {
      const selectedLocation = transferSelections[item.productId];
      if (!selectedLocation) return false;
      const location = item.otherLocationsWithStock.find(l => l.locationId === selectedLocation);
      return location && location.available >= item.shortfall;
    });

  const hasUnresolvableShortfall = stockCheck.items.some(
    item => item.shortfall > 0 && item.otherLocationsWithStock.length === 0
  );

  return (
    <Box sx={{ mb: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <InventoryIcon sx={{ color: palette.primary }} />
        <Typography
          sx={{
            fontFamily: typography.fontFamily.display,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            textTransform: 'uppercase',
            fontSize: typography.fontSize.sm,
          }}
        >
          Vérification du stock
        </Typography>
      </Box>

      {/* Location info */}
      <Alert
        severity="info"
        sx={{ mb: 2, bgcolor: alpha(palette.info, 0.1), border: `1px solid ${palette.info}` }}
      >
        <Typography variant="body2">
          <strong>Point de vente:</strong> {stockCheck.locationName}
        </Typography>
      </Alert>

      {/* Status summary */}
      {stockCheck.canConfirm ? (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon />}
          sx={{ mb: 2, bgcolor: alpha(palette.success, 0.1), border: `1px solid ${palette.success}` }}
        >
          <Typography variant="body2" fontWeight={600}>
            Stock suffisant
          </Typography>
          <Typography variant="body2">
            Tous les articles sont disponibles au point de vente.
          </Typography>
        </Alert>
      ) : hasUnresolvableShortfall ? (
        <Alert
          severity="error"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2, bgcolor: alpha(palette.error, 0.1), border: `1px solid ${palette.error}` }}
        >
          <Typography variant="body2" fontWeight={600}>
            Stock insuffisant
          </Typography>
          <Typography variant="body2">
            Certains articles ne sont disponibles nulle part. La commande ne peut pas être confirmée.
          </Typography>
        </Alert>
      ) : (
        <Alert
          severity="warning"
          icon={<SwapHorizIcon />}
          sx={{ mb: 2, bgcolor: alpha(palette.warning, 0.1), border: `1px solid ${palette.warning}` }}
        >
          <Typography variant="body2" fontWeight={600}>
            Transfert de stock requis
          </Typography>
          <Typography variant="body2">
            Certains articles doivent être transférés d'autres emplacements.
          </Typography>
        </Alert>
      )}

      {/* Stock table */}
      <TableContainer
        sx={{
          borderRadius: customTheme.radius.sm,
          border: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
          mb: 2,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: colors.surfaceElevated }}>
              <TableCell
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                Produit
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                Demandé
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                Disponible
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                Manque
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                Source transfert
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stockCheck.items.map((item) => {
              const hasShortfall = item.shortfall > 0;
              const hasAlternatives = item.otherLocationsWithStock.length > 0;

              return (
                <TableRow
                  key={item.productId}
                  sx={{
                    borderLeft: `4px solid ${
                      !hasShortfall ? palette.success :
                      hasAlternatives ? palette.warning :
                      palette.error
                    }`,
                    '&:hover': {
                      bgcolor: alpha(colors.border, 0.3),
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: typography.fontSize.sm,
                        fontWeight: typography.fontWeight.medium,
                        color: colors.textPrimary,
                      }}
                    >
                      {item.productName}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={item.requiredQuantity}
                      size="small"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.bold,
                        bgcolor: colors.surfaceElevated,
                        border: `1px solid ${colors.border}`,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={item.availableAtLocation}
                      size="small"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.bold,
                        bgcolor: item.availableAtLocation >= item.requiredQuantity
                          ? alpha(palette.success, 0.1)
                          : alpha(palette.error, 0.1),
                        color: item.availableAtLocation >= item.requiredQuantity
                          ? palette.success
                          : palette.error,
                        border: `1px solid ${
                          item.availableAtLocation >= item.requiredQuantity
                            ? palette.success
                            : palette.error
                        }`,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {hasShortfall ? (
                      <Chip
                        label={`-${item.shortfall}`}
                        size="small"
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.bold,
                          bgcolor: alpha(palette.error, 0.1),
                          color: palette.error,
                          border: `1px solid ${palette.error}`,
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: colors.textMuted }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {hasShortfall && (
                      hasAlternatives ? (
                        <FormControl size="small" fullWidth>
                          <Select
                            value={transferSelections[item.productId] || ''}
                            onChange={(e) => handleSelectionChange(item.productId, e.target.value)}
                            sx={{
                              fontSize: typography.fontSize.xs,
                              '& .MuiSelect-select': {
                                py: 0.5,
                              },
                            }}
                          >
                            {item.otherLocationsWithStock.map((loc) => (
                              <MenuItem
                                key={loc.locationId}
                                value={loc.locationId}
                                disabled={loc.available < item.shortfall}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                  <Typography sx={{ fontSize: typography.fontSize.xs }}>
                                    {loc.locationName}
                                  </Typography>
                                  <Chip
                                    label={loc.available}
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      height: 20,
                                      fontSize: '10px',
                                      bgcolor: loc.available >= item.shortfall
                                        ? alpha(palette.success, 0.1)
                                        : alpha(palette.warning, 0.1),
                                      color: loc.available >= item.shortfall
                                        ? palette.success
                                        : palette.warning,
                                    }}
                                  />
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip
                          label="Aucun stock disponible"
                          size="small"
                          sx={{
                            bgcolor: alpha(palette.error, 0.1),
                            color: palette.error,
                            fontSize: typography.fontSize.xs,
                          }}
                        />
                      )
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {stockCheck.canConfirm ? (
          <Button
            variant="contained"
            startIcon={isConfirming ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
            onClick={onConfirm}
            disabled={isConfirming}
            sx={{
              bgcolor: palette.success,
              '&:hover': { bgcolor: palette.successDark },
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
            }}
          >
            {isConfirming ? 'Confirmation...' : 'Confirmer la commande'}
          </Button>
        ) : stockCheck.requiresTransfer && allShortfallsResolved ? (
          <Button
            variant="contained"
            startIcon={isConfirming ? <CircularProgress size={16} color="inherit" /> : <LocalShippingIcon />}
            onClick={handleConfirmWithTransfers}
            disabled={isConfirming}
            sx={{
              bgcolor: palette.warning,
              '&:hover': { bgcolor: palette.warningDark },
              fontFamily: typography.fontFamily.mono,
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontSize: typography.fontSize.xs,
            }}
          >
            {isConfirming ? 'En cours...' : 'Demander transfert et confirmer'}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};
